import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, Card, Tag, Button, Tooltip, Modal, Typography, Popconfirm } from "antd";
import {
  CalendarOutlined,
  VideoCameraOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  CheckCircleFilled,
  CloseOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import ModelWrapper from "../../../modal/ModelWrapper";
import { CustomButton, CustomSecondaryButton } from "../styled";
import { extractToken } from "../../../../utils/localStorage";
import { globalRoles } from "../../../../utils/roles";
import styled from "styled-components";
import VideoPreviewModal from "../../admin/videos/VideoPreviewModal";
import mailSchedular from "../../../../services/mailSchedular";
import { notify } from "../../../../utils/helper";

const { Title, Text, Paragraph } = Typography;

const StyleWrapper = styled.div`
  .ant-typography-copy {
    color: #8a3ef4;
  }
`;

const PatientDetailModal = ({
  isOpen,
  setIsOpen,
  selectedPatientDetails,
  scheduledMails,
  isLoading,
  setSelectedPatientForSchedule,
  setIsScheduleModalOpen,
  setSelectedMailForReschedule,
  setIsRescheduleModalOpen,
  cancelScheduledMail,
  rescheduleScheduledMail,
  setSelectedPatientDetails,
  getScheduledMailList,
}) => {
  const user = extractToken();
  const [isVideoPreviewModalVisible, setIsVideoPreviewModalVisible] = useState(false);
  const [selectedVideoForPreview, setSelectedVideoForPreview] = useState(null);

  const handleVideoPreview = (mail) => {
    // console.log("mail for video preview", mail);
    setSelectedVideoForPreview({
      videoUrl: mail?.metadata?.videoUrl,
      title: mail?.metadata?.videoName
    });
    setIsVideoPreviewModalVisible(true);
  };

  // Suppress ResizeObserver errors
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (
        typeof args[0] === 'string' &&
        args[0].includes('ResizeObserver loop completed with undelivered notifications')
      ) {
        return;
      }
      originalError.call(console, ...args);
    };

    // Cleanup function to restore original console.error
    return () => {
      console.error = originalError;
    };
  }, []);

  const handleCancelEmail = (mailId) => {
    // Delay the action to allow Popconfirm to close properly
    setTimeout(() => {
      cancelScheduledMail(mailId);
    }, 100);
  };

  const handleDeleteEmail = async (mailId) => {
    // Delay the action to allow Popconfirm to close properly
    setTimeout(async () => {
      try {
        await mailSchedular.deleteMailById(mailId);
        notify("success", "Email deleted successfully!");
        getScheduledMailList();
      } catch (error) {
        console.error("Error deleting email:", error);
      }
    }, 100);
  };

  const patientScheduledMails = useMemo(() => {
    if (!selectedPatientDetails || !Array.isArray(scheduledMails)) {
      return [];
    }
    return scheduledMails.filter(
      (mail) => mail?.patientId === selectedPatientDetails.id
    );
  }, [selectedPatientDetails, scheduledMails, getScheduledMailList]);

  const getStatusTag = (status) => {
    return (
      <Tag color={status ? "green" : "red"}>
        {status ? "Active" : "Inactive"}
      </Tag>
    );
  };

  return (
    <ModelWrapper
      title={
        <div className="row-flex flex-column">
          <h3 className="font-18 font-semibold text-primary mb-5">
            Patient Details
          </h3>
          <p className="font-14 text-secondry mb-0">
            View patient information and scheduled emails
          </p>
        </div>
      }
      footer={null}
      show={isOpen}
      setShow={setIsOpen}
      width={700}
      keyClose={false}
      className="custom-detail-modal"
    >
      {selectedPatientDetails && (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card
              type="inner"
              className="mb-16"
              style={{ border: "1px solid var(--border-color)" }}
            >
              <Row gutter={[16, 12]}>
                <Col span={12}>
                  <div className="font-14 text-secondry">Name</div>
                  <div className="font-16 font-medium text-primary">
                    {selectedPatientDetails.name}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="font-14 text-secondry">Email</div>
                  <div className="font-16 font-medium text-primary ellipsis">
                    {selectedPatientDetails.email}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="font-14 text-secondry">Mobile Number</div>
                  <div className="font-16 font-medium text-primary">
                    {selectedPatientDetails?.mobile?.number ?
                      `${selectedPatientDetails.mobile.countryCode} ${selectedPatientDetails.mobile.number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}` :
                      'Not provided'
                    }
                  </div>
                </Col>
                <Col span={12}>
                  <Row justify="start">
                    <Col>
                      <div className="font-14 text-secondry">Status</div>
                      <div>{getStatusTag(selectedPatientDetails.status)}</div>
                    </Col>
                    <Col style={{ marginLeft: "15px" }}>
                      <div className="font-14 text-secondry">
                        Email Verified
                      </div>
                      <div>
                        <Tag
                          color={
                            selectedPatientDetails.isEmailVerified
                              ? "green"
                              : "orange"
                          }
                        >
                          {selectedPatientDetails.isEmailVerified
                            ? "Verified"
                            : "Not Verified"}
                        </Tag>
                      </div>
                    </Col>
                  </Row>
                </Col>

                {(user?.role === globalRoles.SUPERADMIN ||
                  user?.role === globalRoles.LIBRARY_MANAGER) && (
                    <Col span={12}>
                      <StyleWrapper>
                        <div className="font-14 text-secondry">User Id</div>
                        <div>
                          <Paragraph copyable style={{ marginBottom: 0 }} className="text-primary">
                            {selectedPatientDetails.id}
                          </Paragraph>
                        </div>
                      </StyleWrapper>
                    </Col>
                  )}
              </Row>
            </Card>
          </Col>

          <Col span={24}>
            <div
              className="d-flex align-items-center justify-space-between"
              style={{ marginBottom: "8px" }}
            >
              <div className="font-16 font-semibold text-primary">
                Scheduled Emails ({patientScheduledMails.length})
              </div>
              {patientScheduledMails.length > 0 && (
                <Tag color="blue" style={{ marginLeft: "10px" }}>
                  <CalendarOutlined style={{ marginRight: "5px" }} />
                  Active Schedules
                </Tag>
              )}
            </div>
            <Card
              type="inner"
              style={{ border: "1px solid var(--border-color)" }}
            >
              {patientScheduledMails.length > 0 ? (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {patientScheduledMails.map((mail) => (
                    <Card
                      key={`${mail.id}-${mail.scheduledTime}`}
                      type="inner"
                      className="mb-2"
                      style={{
                        border: "1px solid var(--border-color)",
                        borderRadius: "8px",
                        borderLeft: `4px solid ${mail.delivery.email.status === "sent"
                            ? "#52c41a"
                            : mail.delivery.email.status === "scheduled"
                              ? "#1890ff"
                              : "#faad14"
                          }`,
                      }}
                    >
                      <div className="mb-12">
                        <Row
                          align="middle"
                          justify="space-between"
                          style={{ width: "100%" }}
                        >
                          {/* Top Row - Video Info and Status */}
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "10px", width: "100%", justifyContent: "center" }}
                          >
                            {/* Video Info */}
                            <div
                              className="d-flex align-items-center justify-start"
                              style={{ gap: "8px", width: "90%" }}
                            >
                              <div
                                style={{
                                  backgroundColor: "#f0f2ff",
                                  borderRadius: "6px",
                                  padding: "6px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "28px",
                                  height: "28px",
                                }}
                              >
                                <VideoCameraOutlined
                                  style={{
                                    fontSize: "14px",
                                    color: "#8A3EF4",
                                  }}
                                />
                              </div>
                              <div
                                className="font-14 font-semibold text-primary ellipsis"
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  color: "#1890ff"
                                }}
                                onClick={() => handleVideoPreview(mail)}
                              >
                                {mail.metadata.videoName}
                              </div>
                            </div>

                            {/* Status Tag */}
                            <Tag
                              color={
                                mail.delivery.email.status === "sent"
                                  ? "green"
                                  : mail.delivery.email.status === "scheduled"
                                    ? "blue"
                                    : "orange"
                              }
                              style={{
                                fontSize: "11px",
                                marginLeft: "8px",
                              }}
                            >
                              {mail.delivery.email.status.toUpperCase()}
                            </Tag>
                          </div>

                          {/* Left Side - Scheduled Info */}
                          <Col span={10}>
                            <div style={{ gap: "8px" }}>
                              <div>
                                <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "8px 12px",
                                    backgroundColor: "var(--secondary-bg, #f8f9fa)",
                                    borderRadius: "6px",
                                    border: "1px solid var(--border-color, #e9ecef)"
                                  }}>
                                    <CalendarOutlined
                                      style={{
                                        fontSize: "14px",
                                        color: "var(--primary-color, #9D5CF9)",
                                        minWidth: "14px"
                                      }}
                                    />
                                    <span style={{
                                      fontSize: "12px",
                                      color: "var(--secondary-text-color, #8c8c8c)",
                                      fontWeight: "500",
                                      minWidth: "70px"
                                    }}>
                                      Scheduled:
                                    </span>
                                    <span style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "var(--primary-text-color, #262626)"
                                    }}>
                                      {moment(mail.scheduledTime).format("DD MMM, HH:mm")}
                                    </span>
                                  </div>

                                  {mail?.frequency && (
                                    <div style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      padding: "8px 12px",
                                      backgroundColor: "var(--secondary-bg, #f8f9fa)",
                                      borderRadius: "6px",
                                      border: "1px solid var(--border-color, #e9ecef)"
                                    }}>
                                      <CalendarOutlined
                                        style={{
                                          fontSize: "14px",
                                          color: "var(--primary-color, #9D5CF9)",
                                          minWidth: "14px"
                                        }}
                                      />
                                      <span style={{
                                        fontSize: "12px",
                                        color: "var(--secondary-text-color, #8c8c8c)",
                                        fontWeight: "500",
                                        minWidth: "70px"
                                      }}>
                                        Frequency:
                                      </span>
                                      <span style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "var(--primary-text-color, #262626)"
                                      }}>
                                        {mail?.frequency === "custom"
                                          ? `${mail?.customDays} days`
                                          : mail?.frequency}
                                      </span>
                                    </div>
                                  )}

                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "8px 12px",
                                    backgroundColor: "var(--secondary-bg, #f8f9fa)",
                                    borderRadius: "6px",
                                    border: "1px solid var(--border-color, #e9ecef)"
                                  }}>
                                    <ClockCircleOutlined
                                      style={{
                                        fontSize: "14px",
                                        color: "var(--primary-color, #9D5CF9)",
                                        minWidth: "14px"
                                      }}
                                    />
                                    <span style={{
                                      fontSize: "12px",
                                      color: "var(--secondary-text-color, #8c8c8c)",
                                      fontWeight: "500",
                                      minWidth: "70px"
                                    }}>
                                      Created:
                                    </span>
                                    <span style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "var(--primary-text-color, #262626)"
                                    }}>
                                      {moment(mail.createdAt).format("DD MMM, HH:mm")}
                                    </span>
                                  </div>


                                </div>
                              </div>
                            </div>
                          </Col>

                          {/* Right Side - Video Info and Status */}
                          <Col span={14}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "12px", marginTop: "7px" }}>

                              {/* Bottom Row - Video Watched Information and Notification Type */}
                              <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: "8px",
                                minWidth: "200px"
                              }}>
                                {/* Notification Type */}
                                <div style={{
                                  display: "flex",
                                  // flexDirection: "column", 
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "4px",
                                  padding: "7px 12px",
                                  backgroundColor: "var(--secondary-bg, #f8f9fa)",
                                  borderRadius: "6px",
                                  border: "1px solid var(--border-color, #e9ecef)",
                                  width: "100%"
                                }}>
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontSize: "12px",
                                    color: "var(--secondary-text-color, #8c8c8c)",
                                    fontWeight: "500",
                                    minWidth: "110px"
                                  }}>
                                    <InfoCircleOutlined style={{ fontSize: "12px" }} />
                                    Delivery Type:
                                  </div>
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    justifyContent: "flex-end"
                                  }}>
                                    {mail.delivery?.email?.enabled && (
                                      <Tag color="blue" style={{ fontSize: "10px", padding: "1px 6px" }}>
                                        Email
                                      </Tag>
                                    )}
                                    {mail.delivery?.sms?.enabled && (
                                      <Tag color="green" style={{ fontSize: "10px", padding: "1px 6px" }}>
                                        SMS
                                      </Tag>
                                    )}
                                    {!mail.delivery?.email?.enabled && !mail.delivery?.sms?.enabled && (
                                      <span style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "var(--secondary-text-color, #8c8c8c)"
                                      }}>
                                        -
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Video Watched Information */}
                                <div style={{
                                  display: "flex",
                                  // flexDirection: "column", 
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "4px",
                                  padding: "7px 12px",
                                  backgroundColor: "var(--secondary-bg, #f8f9fa)",
                                  borderRadius: "6px",
                                  border: "1px solid var(--border-color, #e9ecef)",
                                  width: "100%"
                                }}>
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontSize: "12px",
                                    color: "var(--secondary-text-color, #8c8c8c)",
                                    fontWeight: "500",
                                    minWidth: "183px"
                                  }}>
                                    <EyeOutlined style={{ fontSize: "12px" }} />
                                    Video Watched:
                                    <Tooltip title="Email or message will not be sent to the patient after viewing this video">
                                      <InfoCircleOutlined
                                        style={{
                                          marginLeft: "4px",
                                          cursor: "pointer",
                                          fontSize: "11px"
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                  <div style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: mail?.isWatch ? "#52c41a" : "var(--primary-text-color, #262626)",
                                    textAlign: "right"
                                  }}>
                                    {mail?.isWatch ? (
                                      <div>
                                        <div>✓ Yes</div>
                                        {mail?.watchTimestamp && (
                                          <div style={{
                                            fontSize: "11px",
                                            fontWeight: "400",
                                            color: "var(--secondary-text-color, #8c8c8c)",
                                            marginTop: "2px"
                                          }}>
                                            {moment(mail?.watchTimestamp).format("DD MMM YYYY, HH:mm")}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      "No"
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {/* Footer Row */}
                      <div className="d-flex justify-space-between align-items-center">
                        <div style={{ marginRight: "10px" }}>
                          {mail.delivery?.email?.status === "scheduled" && (
                            <div
                              className="font-12 mt-2"
                              style={{ color: "var(--primary-color, #9D5CF9)" }}
                            >
                              <ClockCircleOutlined
                                style={{
                                  marginRight: "4px",
                                  marginTop: "10px",
                                  fontSize: "11px",
                                }}
                              />
                              {moment(mail.scheduledTime).fromNow()}
                            </div>
                          )}
                          {mail.delivery?.email?.status === "sent" && (
                            <div
                              className="font-12"
                              style={{ color: "#52c41a" }}
                            >
                              <CheckCircleFilled
                                style={{
                                  marginRight: "4px",
                                  marginTop: "10px",
                                  fontSize: "11px",
                                }}
                              />
                              Email sent
                            </div>
                          )}
                        </div>


                        <div
                          className="d-flex align-items-center"
                          style={{ gap: "6px", marginTop: "10px" }}
                        >
                          {/* Show Reschedule for all emails except canceled */}
                          {mail.delivery?.email?.status !== "canceled" && mail.delivery?.email?.status !== "cancelled" && (

                            <Tooltip title="Reschedule">
                              <Button
                                type="text"
                                size="small"
                                icon={<CalendarOutlined />}
                                onClick={() => {
                                  setSelectedMailForReschedule(mail);
                                  setIsRescheduleModalOpen(true);
                                }}
                                disabled={isLoading}
                                style={{
                                  padding: "4px 8px",
                                  height: "28px",
                                  fontSize: "12px",
                                  color: "var(--primary-text-color)"
                                }}
                              >
                                Reschedule
                              </Button>
                            </Tooltip>
                          )}

                          {/* Show Cancel for all emails except canceled */}
                          {mail.delivery?.email?.status !== "canceled" && mail.delivery?.email?.status !== "cancelled" && (
                            <Popconfirm
                              title="Cancel Email"
                              description={`Cancel email for "${mail.metadata.videoName}"?`}
                              onConfirm={() => handleCancelEmail(mail.id)}
                              okText="Yes, Cancel"
                              cancelText="No"
                              okType="danger"
                            >
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<CloseOutlined />}
                                disabled={isLoading}
                                style={{
                                  padding: "4px 8px",
                                  height: "28px",
                                  fontSize: "12px",
                                }}
                              >
                                Cancel
                              </Button>
                            </Popconfirm>
                          )}

                          {/* Delete button is always available for all emails */}
                          <Popconfirm
                            title="Delete Email"
                            description={`Permanently delete email for "${mail.metadata.videoName}"?`}
                            onConfirm={() => handleDeleteEmail(mail.id)}
                            okText="Yes, Delete"
                            cancelText="No"
                            okType="danger"
                          >
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              disabled={isLoading}
                              style={{
                                padding: "4px 8px",
                                height: "28px",
                                fontSize: "12px",
                              }}
                            >
                              Delete
                            </Button>
                          </Popconfirm>
                        </div>
                      </div>
                      {globalRoles?.SUPERADMIN === user?.role && <div style={{ display: 'flex', marginTop: "5px" }}>
                        <p style={{
                          fontSize: "11px",
                          fontWeight: "400",
                          color: "var(--secondary-text-color, #8c8c8c)",
                        }}>
                          Sent By : {mail?.sentBy?.email}
                        </p>
                      </div>}
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center" style={{ padding: "40px 20px" }}>
                  <CalendarOutlined
                    style={{
                      fontSize: "32px",
                      color: "#d9d9d9",
                      marginBottom: "12px",
                    }}
                  />
                  <div className="font-16 text-secondry">
                    No scheduled emails
                  </div>
                  <div className="font-14 text-secondry">
                    Schedule an email to see it here
                  </div>
                </div>
              )}
            </Card>
          </Col>

          <Col span={24} className="d-flex justify-end gap-12 mt-16">
            <CustomSecondaryButton
              onClick={() => {
                setIsOpen(false);
                setSelectedPatientDetails(null);
              }}
              size="large"
            >
              Close
            </CustomSecondaryButton>
            <CustomButton
              onClick={() => {
                setIsOpen(false);
                setSelectedPatientForSchedule(selectedPatientDetails.id);
                setIsScheduleModalOpen(true);
              }}
              size="large"
            >
              Schedule New Email
            </CustomButton>
          </Col>
        </Row>
      )}

      {isVideoPreviewModalVisible && (
        <VideoPreviewModal
          isVisible={isVideoPreviewModalVisible}
          setIsVisible={setIsVideoPreviewModalVisible}
          onClose={() => {
            setIsVideoPreviewModalVisible(false);
            setSelectedVideoForPreview(null);
          }}
          videoUrl={selectedVideoForPreview?.videoUrl}
          title={selectedVideoForPreview?.title}
        />
      )}
    </ModelWrapper>
  );
};

export default PatientDetailModal;
